/**
 * BrandedModal Component
 * 
 * A customizable modal component supporting success, warning, and error states.
 * Features an "Auto-Confirm" mode (Waze-style) with a progress bar button that
 * automatically triggers the confirm action after a set duration.
 */
'use client';

import { useEffect, useState, useRef } from 'react';

type ModalType = 'warning' | 'success' | 'error';

interface BrandedModalProps {
    isOpen: boolean;
    type: ModalType;
    title: string;
    message: string | React.ReactNode;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    autoConfirm?: boolean;
    autoConfirmDuration?: number;
}

export default function BrandedModal({
    isOpen,
    type,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    autoConfirm = false,
    autoConfirmDuration = 10000
}: BrandedModalProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [progress, setProgress] = useState(0);
    const onConfirmRef = useRef(onConfirm);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        onConfirmRef.current = onConfirm;
    }, [onConfirm]);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
        } else {
            const timer = setTimeout(() => setIsVisible(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Handle Auto-Confirm timer and progress bar
    useEffect(() => {
        if (isOpen && autoConfirm && type === 'success') {
            // Logic simplified: prop-driven auto-confirmation
        }

        let interval: NodeJS.Timeout;
        if (isOpen && autoConfirm) {
            setProgress(0);
            const startTime = Date.now();
            const duration = autoConfirmDuration;

            interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const newProgress = Math.min((elapsed / duration) * 100, 100);
                setProgress(newProgress);

                if (elapsed >= duration) {
                    clearInterval(interval);
                    if (onConfirmRef.current) onConfirmRef.current();
                }
            }, 50);
        } else {
            setProgress(0);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isOpen, autoConfirm, autoConfirmDuration]);

    if (!isVisible) return null;

    const colors = {
        warning: {
            bg: 'bg-safety-yellow-pantone-107c',
            text: 'text-eg-grey-pantone-447c',
            icon: 'text-eg-grey-pantone-447c',
            button: 'bg-eg-green-pantone-368c text-white hover:opacity-90',
            border: 'border-safety-yellow-pantone-107c'
        },
        success: {
            bg: 'bg-eg-green-pantone-368c',
            text: 'text-white',
            icon: 'text-white',
            button: 'bg-eg-green-pantone-368c text-white hover:brightness-90',
            border: 'border-eg-green-pantone-368c'
        },
        error: {
            bg: 'bg-prohibitive-red-pantone-485c',
            text: 'text-white',
            icon: 'text-white',
            button: 'bg-white text-prohibitive-red-pantone-485c hover:bg-neutral-100',
            border: 'border-prohibitive-red-pantone-485c'
        }
    };

    const theme = colors[type];

    return (
        <div
            className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100 backdrop-blur-sm bg-black/40' : 'opacity-0 pointer-events-none'}`}
        >
            <div
                className={`w-full max-w-sm transform transition-all duration-300 ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'} bg-white dark:bg-neutral-800 rounded-3xl shadow-2xl overflow-hidden border-2 ${theme.border}`}
            >
                {/* Header Icon Area */}
                <div className={`w-full py-8 flex items-center justify-center ${theme.bg}`}>
                    {type === 'warning' && (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-16 h-16 ${theme.icon} animate-pulse`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                    )}
                    {type === 'success' && (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-16 h-16 ${theme.icon} animate-[breath_2s_ease-in-out_infinite]`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                    )}
                    {type === 'error' && (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-16 h-16 ${theme.icon}`}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                        </svg>
                    )}
                </div>

                {/* Content */}
                <div className="p-8 text-center bg-white dark:bg-neutral-800">
                    <h3 className="text-2xl font-bold font-brandon text-eg-grey-pantone-447c dark:text-white mb-2 uppercase tracking-wide">
                        {title}
                    </h3>
                    <div className="text-neutral-500 dark:text-neutral-400 font-sans text-lg leading-relaxed">
                        {message}
                    </div>

                    {/* Actions */}
                    <div className="grid grid-cols-1 gap-3 mt-8">
                        {onConfirm && (
                            <button
                                onClick={onConfirm}
                                className={`relative w-full py-3.5 rounded-xl text-lg font-bold uppercase tracking-wider transition-all active:scale-95 shadow-lg ${theme.button} overflow-hidden`}
                            >
                                {/* Progress Bar Background */}
                                {autoConfirm && (
                                    <div
                                        className="absolute inset-0 bg-black/20 transition-all duration-75 ease-linear"
                                        style={{ width: `${progress}%` }}
                                    />
                                )}
                                <span className="relative z-10">{confirmText}</span>
                            </button>
                        )}

                        {onCancel && (
                            <button
                                onClick={onCancel}
                                className="w-full py-3.5 rounded-xl text-lg font-bold uppercase tracking-wider transition-all active:scale-95 shadow-lg bg-prohibitive-red-pantone-485c text-white hover:brightness-90"
                            >
                                {cancelText}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
